<template>
  <pwa-layout subtitle="ESPACE PRO" :has-notif="true">
    <div class="pwa-home">

      <!-- ── Partie fixe : hero + raccourcis ── -->
      <div class="pwa-fixed-top">
        <div class="pwa-hero">
          <div class="pwa-hero__top">
            <span class="pwa-hero__label">TRANSACTIONS CE MOIS</span>
            <span class="pwa-hero__badge">Flotte Active</span>
          </div>
          <div class="pwa-hero__amount">{{ formatAmount(totalTransactions) }}</div>
          <div class="pwa-hero__currency">F CFA</div>
          <div class="pwa-hero__meta">
            <span class="pwa-hero__meta-item">
              <span class="mdi mdi-swap-horizontal"></span>
              {{ recentTx.length }} transactions récentes
            </span>
          </div>
        </div>

        <div v-if="caps.canSeeVehicles || caps.canSeeVouchers" class="pwa-shortcuts">
          <div
            v-if="caps.canSeeVehicles"
            class="pwa-shortcut-card"
            @click="$router.push('/pwa/vehicles')"
          >
            <div class="pwa-shortcut-row">
              <div class="pwa-shortcut-icon"><span class="mdi mdi-car"></span></div>
              <div class="pwa-shortcut-value">{{ homeData.vehicles_total || 0 }}</div>
            </div>
            <div class="pwa-shortcut-label">Véhicules</div>
            <span class="mdi mdi-chevron-right pwa-shortcut-arrow"></span>
          </div>
          <div
            v-if="caps.canSeeVouchers"
            class="pwa-shortcut-card pwa-shortcut-card--slate"
            @click="$router.push('/pwa/vouchers')"
          >
            <div class="pwa-shortcut-row">
              <div class="pwa-shortcut-icon"><span class="mdi mdi-gas-station"></span></div>
              <div class="pwa-shortcut-value">{{ homeData.voucher_active || 0 }}</div>
            </div>
            <div class="pwa-shortcut-label">Crédit carburant</div>
            <span class="mdi mdi-chevron-right pwa-shortcut-arrow"></span>
          </div>
        </div>

        <div class="pwa-section-header">
          <span class="pwa-section-title">Dernières transactions</span>
          <router-link to="/pwa/history" class="pwa-link">Tout voir</router-link>
        </div>
      </div>

      <!-- ── Liste scrollable ── -->
      <div class="pwa-tx-list">
        <div v-if="loadingTx" class="pwa-loading">
          <span class="mdi mdi-loading mdi-spin"></span>
        </div>
        <template v-else>
          <div v-for="tx in recentTx" :key="tx.id" class="pwa-tx-card" @click="openDetail(tx)">
            <div class="pwa-tx-icon" :class="txIconClass(tx)">
              <span class="mdi" :class="txIcon(tx)"></span>
            </div>
            <div class="pwa-tx-info">
              <div class="pwa-tx-title">{{ txTitle(tx) }}</div>
              <div class="pwa-tx-sub">
                {{ timeAgo(tx.created_at) }}
                <span v-if="tx.quantity"> · {{ tx.quantity }} L</span>
              </div>
            </div>
            <div class="pwa-tx-right">
              <span class="pwa-tx-amount">-{{ formatAmount(Math.abs(tx.amount || 0)) }} F</span>
            </div>
          </div>
          <div v-if="!recentTx.length" class="pwa-empty">Aucune transaction récente</div>
        </template>
      </div>

    </div>

    <!-- ── FAB flottant (uniquement si l utilisateur peut creer qqch) ── -->
    <template v-if="caps.canCreateVehicle || caps.canCreateVoucher" slot="fab">
      <div v-if="fabOpen" class="pwa-fab-overlay" @click="fabOpen = false"></div>
      <div class="pwa-fab-wrap">
        <transition name="fab-items">
          <div v-if="fabOpen" class="pwa-fab-items">
            <button
              v-if="caps.canCreateVehicle"
              class="pwa-fab-item"
              @click="fabOpen = false; $router.push('/pwa/vehicles/new')"
            >
              <span class="pwa-fab-item__icon pwa-fab-item__icon--orange">
                <span class="mdi mdi-car"></span>
              </span>
              <span class="pwa-fab-item__label">Nouveau véhicule</span>
            </button>
            <button
              v-if="caps.canCreateVoucher"
              class="pwa-fab-item"
              @click="fabOpen = false; $router.push('/pwa/vouchers/new')"
            >
              <span class="pwa-fab-item__icon pwa-fab-item__icon--slate">
                <span class="mdi mdi-gas-station"></span>
              </span>
              <span class="pwa-fab-item__label">Crédit carburant</span>
            </button>
          </div>
        </transition>
        <button class="pwa-fab" :class="{ 'pwa-fab--open': fabOpen }" @click="fabOpen = !fabOpen">
          <span class="mdi mdi-plus pwa-fab-icon"></span>
        </button>
      </div>
    </template>

    <!-- ── Modal détail transaction ── -->
    <div v-if="selectedTx" class="pwa-modal-overlay" @click.self="selectedTx = null">
      <div class="pwa-modal">
        <div class="pwa-modal-header">
          <span class="pwa-modal-title">Détail transaction</span>
          <button class="pwa-modal-close" @click="selectedTx = null">
            <span class="mdi mdi-close"></span>
          </button>
        </div>
        <div class="pwa-modal-body">
          <div class="pwa-detail-amount">
            <span class="pwa-detail-amount__value">{{ formatAmount(Math.abs(selectedTx.amount || 0)) }}</span>
            <span class="pwa-detail-amount__cur"> F CFA</span>
          </div>
          <div class="pwa-detail-rows">
            <div v-if="selectedTx.vehicle" class="pwa-detail-row">
              <span class="pwa-detail-row__label">Véhicule</span>
              <span class="pwa-detail-row__val">{{ selectedTx.vehicle.registration_number || '—' }}</span>
            </div>
            <div v-if="selectedTx.vehicle && selectedTx.vehicle.driver_name" class="pwa-detail-row">
              <span class="pwa-detail-row__label">Chauffeur</span>
              <span class="pwa-detail-row__val">{{ selectedTx.vehicle.driver_name }}</span>
            </div>
            <div v-if="selectedTx.vehicle && selectedTx.vehicle.company" class="pwa-detail-row">
              <span class="pwa-detail-row__label">Société</span>
              <span class="pwa-detail-row__val">{{ selectedTx.vehicle.company }}</span>
            </div>
            <div v-else-if="selectedTx.donation_used && selectedTx.donation_used.company_name" class="pwa-detail-row">
              <span class="pwa-detail-row__label">Société</span>
              <span class="pwa-detail-row__val">{{ selectedTx.donation_used.company_name }}</span>
            </div>
            <div class="pwa-detail-row">
              <span class="pwa-detail-row__label">Produit</span>
              <span class="pwa-detail-row__val">{{ selectedTx.product || 'Carburant' }}</span>
            </div>
            <div v-if="selectedTx.quantity" class="pwa-detail-row">
              <span class="pwa-detail-row__label">Quantité</span>
              <span class="pwa-detail-row__val">{{ selectedTx.quantity }} L</span>
            </div>
            <div v-if="selectedTx.station_name || selectedTx.shop" class="pwa-detail-row">
              <span class="pwa-detail-row__label">Station</span>
              <span class="pwa-detail-row__val">{{ selectedTx.station_name || selectedTx.shop }}</span>
            </div>
            <div class="pwa-detail-row">
              <span class="pwa-detail-row__label">Date</span>
              <span class="pwa-detail-row__val">{{ formatDate(selectedTx.created_at) }}</span>
            </div>
          </div>

          <!-- Preuves photos -->
          <div v-if="selectedTx.image || selectedTx.vehicle_image" class="pwa-detail-proofs">
            <div class="pwa-detail-proofs__title">
              <span class="mdi mdi-image-multiple"></span>
              Preuves photos
            </div>
            <div class="pwa-detail-proofs__links">
              <a
                v-if="selectedTx.image"
                :href="selectedTx.image"
                target="_blank"
                rel="noopener"
                class="pwa-proof-btn"
              >
                <span class="mdi mdi-gas-station"></span>
                Photo pompe
                <span class="mdi mdi-open-in-new pwa-proof-btn__ext"></span>
              </a>
              <a
                v-if="selectedTx.vehicle_image"
                :href="selectedTx.vehicle_image"
                target="_blank"
                rel="noopener"
                class="pwa-proof-btn"
              >
                <span class="mdi mdi-car"></span>
                Photo véhicule
                <span class="mdi mdi-open-in-new pwa-proof-btn__ext"></span>
              </a>
            </div>
          </div>

        </div>
      </div>
    </div>

  </pwa-layout>
</template>

<script>
import PwaLayout from './PwaLayout.vue'
import BusinessService from '@/services/BusinessService'
import TransactionDetailService from '@/services/TransactionDetailService'

export default {
  name: 'PwaHome',
  components: { PwaLayout },
  data() {
    return {
      homeData: {},
      recentTx: [],
      totalTransactions: 0,
      loadingTx: false,
      selectedTx: null,
      fabOpen: false,
    }
  },
  computed: {
    isBusiness() {
      return this.$store.state.app.company?.company_type === 'Business'
    },
    department() {
      return this.$store.state.app.project
    },
    caps() {
      return this.$store.getters['app/pwaCapabilities']
    },
  },
  mounted() {
    // Si pompiste, on n'a rien a faire ici (le router-guard l'a deja redirige)
    if (this.caps.isPompiste) return
    this.loadHome()
    this.loadRecentTx()
  },
  methods: {
    async loadHome() {
      try {
        const filters = {}
        if (this.department?.id) filters.department = this.department.id
        const data = await BusinessService.getHome(filters)
        this.homeData = {
          vehicles_total: data.vehicles_breakdown?.total || data.vehicles_breakdown?.active || 0,
          voucher_active: data.voucher_breakdown_simple?.in_use || 0,
        }
      } catch (e) {
        console.error(e)
      }
    },
    async loadRecentTx() {
      this.loadingTx = true
      try {
        const params = { ordering: '-created_at', limit: 10 }
        if (this.department?.id) params.department = this.department.id
        const response = await TransactionDetailService.getAll(this, params)
        const inner = response?.results || response
        const list = Array.isArray(inner) ? inner : (inner?.results || [])
        this.totalTransactions = inner?.total_amount || 0
        this.recentTx = list.slice(0, 10)
      } catch (e) {
        console.error(e)
      } finally {
        this.loadingTx = false
      }
    },
    txTitle(tx) {
      const plate = tx.vehicle?.registration_number || ''
      const company = tx.vehicle?.company || tx.donation_used?.company_name || ''
      if (company || plate) {
        const full = company && plate ? `${company} - ${plate}` : (company || plate)
        return full.length > 30 ? full.slice(0, 28) + '…' : full
      }
      return tx.vehicle?.driver_name || 'Transaction'
    },
    formatAmount(v) {
      if (!v && v !== 0) return '0'
      return Math.round(Number(v)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0')
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    timeAgo(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      const now = new Date()
      const diff = now - d
      const mins = Math.floor(diff / 60000)
      const hours = Math.floor(diff / 3600000)
      const days = Math.floor(diff / 86400000)
      if (mins < 60) return `${mins}min`
      if (hours < 24) return `${d.getHours()}h${String(d.getMinutes()).padStart(2, '0')}`
      if (days < 7) return `${days}j`
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
    },
    txIcon(tx) {
      if (tx.product?.toLowerCase().includes('vidange') || tx.product?.toLowerCase().includes('maintenance')) return 'mdi-wrench'
      return 'mdi-gas-station'
    },
    txIconClass(tx) {
      if (tx.product?.toLowerCase().includes('vidange')) return 'pwa-tx-icon--orange'
      return 'pwa-tx-icon--blue'
    },
    openDetail(tx) {
      this.selectedTx = tx
    },
  },
}
</script>

<style scoped>
/* ── Layout principal : flex column, hauteur = viewport - header - nav ── */
.pwa-home {
  display: flex;
  flex-direction: column;
  height: calc(100dvh - var(--pwa-header-h) - var(--pwa-nav-h));
  overflow: hidden;
}

/* ── Partie haute fixe ── */
.pwa-fixed-top {
  flex-shrink: 0;
  background: var(--pwa-bg);
}

/* ── Liste transactions scrollable ── */
.pwa-tx-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 16px;
  min-height: 0;
}

/* Hero */
.pwa-hero {
  margin: 16px 16px 0;
  background: linear-gradient(135deg, #1C2437 0%, #2D3A52 100%);
  border-radius: var(--pwa-card-radius);
  padding: 22px 20px 18px;
  color: white;
}
.pwa-hero__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.pwa-hero__label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1px;
  opacity: 0.7;
  text-transform: uppercase;
}
.pwa-hero__badge {
  font-size: 11px;
  font-weight: 500;
  border: 1px solid rgba(255,255,255,0.3);
  border-radius: 20px;
  padding: 3px 10px;
  opacity: 0.85;
}
.pwa-hero__amount {
  font-size: 38px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -1px;
}
.pwa-hero__currency {
  font-size: 13px;
  opacity: 0.6;
  margin-top: 2px;
  margin-bottom: 14px;
}
.pwa-hero__meta { display: flex; gap: 12px; }
.pwa-hero__meta-item {
  font-size: 12px;
  opacity: 0.7;
  display: flex;
  align-items: center;
  gap: 4px;
}
.pwa-hero__meta-item .mdi { font-size: 14px; }

/* Shortcuts */
.pwa-shortcuts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 12px 16px 0;
}
.pwa-shortcut-card {
  background: linear-gradient(135deg, #E65100 0%, #F57C00 100%);
  border-radius: var(--pwa-card-radius);
  padding: 14px 16px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: transform 0.15s;
}
.pwa-shortcut-card--slate {
  background: linear-gradient(135deg, #37474F 0%, #546E7A 100%);
}
.pwa-shortcut-card:active { transform: scale(0.97); }
.pwa-shortcut-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.pwa-shortcut-icon {
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: rgba(255,255,255,0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 19px;
  color: white;
  flex-shrink: 0;
}
.pwa-shortcut-value {
  font-size: 28px;
  font-weight: 800;
  color: white;
  line-height: 1;
  letter-spacing: -0.5px;
}
.pwa-shortcut-label {
  font-size: 12px;
  color: rgba(255,255,255,0.75);
  font-weight: 500;
}
.pwa-shortcut-arrow {
  position: absolute;
  bottom: 12px;
  right: 10px;
  font-size: 18px;
  color: rgba(255,255,255,0.4);
}
.pwa-shortcut-card::after {
  content: '';
  position: absolute;
  top: -20px;
  right: -20px;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: rgba(255,255,255,0.07);
}

/* Section header */
.pwa-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px 10px;
}
.pwa-section-title { font-size: 16px; font-weight: 700; color: var(--pwa-text); }
.pwa-link { font-size: 13px; font-weight: 500; color: var(--pwa-accent); text-decoration: none; }

/* Transaction card */
.pwa-tx-card {
  background: var(--pwa-white);
  border-radius: var(--pwa-card-radius);
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: background 0.15s;
}
.pwa-tx-card:active { background: #f5f7fa; }
.pwa-tx-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}
.pwa-tx-icon--blue { background: #E3F2FD; color: #1976D2; }
.pwa-tx-icon--orange { background: #FFF3E0; color: #E65100; }
.pwa-tx-info { flex: 1; min-width: 0; }
.pwa-tx-title { font-size: 14px; font-weight: 600; color: var(--pwa-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pwa-tx-sub { font-size: 12px; color: var(--pwa-muted); margin-top: 2px; }
.pwa-tx-right { display: flex; flex-direction: column; align-items: flex-end; flex-shrink: 0; }
.pwa-tx-amount { font-size: 14px; font-weight: 700; color: var(--pwa-text); white-space: nowrap; }

.pwa-loading { text-align: center; padding: 24px; font-size: 24px; color: var(--pwa-muted); }
.pwa-empty { text-align: center; padding: 24px; color: var(--pwa-muted); font-size: 14px; }

/* ── FAB ── */
/* overlay couvre tout le viewport */
.pwa-fab-overlay {
  position: fixed;
  inset: 0;
  z-index: 149;
  background: rgba(0,0,0,0.2);
}
/* wrap positionné en absolute dans .pwa-root (position:relative, max-width:480px) */
.pwa-fab-wrap {
  position: absolute;
  bottom: calc(var(--pwa-nav-h) + 16px);
  right: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  z-index: 150;
}
.pwa-fab {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--pwa-dark);
  border: none;
  box-shadow: 0 4px 16px rgba(28,36,55,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.25s, box-shadow 0.25s;
}
.pwa-fab--open { transform: rotate(45deg); box-shadow: 0 6px 20px rgba(28,36,55,0.5); }
.pwa-fab-icon { font-size: 26px; color: white; }

.pwa-fab-items {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
}
.pwa-fab-item {
  display: flex;
  align-items: center;
  gap: 10px;
  background: white;
  border: none;
  border-radius: 24px;
  padding: 10px 16px 10px 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.15);
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: var(--pwa-text);
  white-space: nowrap;
  transition: transform 0.15s;
}
.pwa-fab-item:active { transform: scale(0.96); }
.pwa-fab-item__icon {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}
.pwa-fab-item__icon--orange { background: #FBE9E7; color: #E65100; }
.pwa-fab-item__icon--slate  { background: #ECEFF1; color: #37474F; }
.pwa-fab-item__label { font-size: 13px; font-weight: 600; color: var(--pwa-text); }

/* Animation d'apparition des items FAB */
.fab-items-enter-active { transition: opacity 0.2s, transform 0.2s; }
.fab-items-leave-active { transition: opacity 0.15s, transform 0.15s; }
.fab-items-enter { opacity: 0; transform: translateY(10px); }
.fab-items-leave-to { opacity: 0; transform: translateY(10px); }

/* Modal */
.pwa-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  z-index: 200;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}
.pwa-modal {
  background: white;
  border-radius: 20px 20px 0 0;
  width: 100%;
  max-width: 480px;
  padding-bottom: env(safe-area-inset-bottom, 16px);
  max-height: 85vh;
  overflow-y: auto;
}
.pwa-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 12px;
  border-bottom: 1px solid #f0f0f0;
}
.pwa-modal-title { font-size: 16px; font-weight: 700; color: var(--pwa-text); }
.pwa-modal-close {
  width: 32px;
  height: 32px;
  border: none;
  background: #f5f5f5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: var(--pwa-muted);
  cursor: pointer;
}
.pwa-modal-body { padding: 20px; }
.pwa-detail-amount { text-align: center; margin-bottom: 24px; }
.pwa-detail-amount__value { font-size: 36px; font-weight: 800; color: var(--pwa-text); letter-spacing: -1px; }
.pwa-detail-amount__cur { font-size: 16px; color: var(--pwa-muted); }
.pwa-detail-rows { display: flex; flex-direction: column; }
.pwa-detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f5f5f5;
}
.pwa-detail-row:last-child { border-bottom: none; }
.pwa-detail-row__label { font-size: 13px; color: var(--pwa-muted); font-weight: 500; }
.pwa-detail-row__val { font-size: 14px; font-weight: 600; color: var(--pwa-text); text-align: right; max-width: 60%; }

/* Preuves photos */
.pwa-detail-proofs {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}
.pwa-detail-proofs__title {
  font-size: 12px;
  font-weight: 600;
  color: var(--pwa-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
}
.pwa-detail-proofs__links {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.pwa-proof-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: #F0F4F8;
  border-radius: 12px;
  text-decoration: none;
  color: var(--pwa-text);
  font-size: 14px;
  font-weight: 600;
  transition: background 0.15s;
}
.pwa-proof-btn:active { background: #E3EAF0; }
.pwa-proof-btn .mdi { font-size: 20px; color: var(--pwa-accent); }
.pwa-proof-btn__ext { margin-left: auto; font-size: 16px; color: var(--pwa-muted); }
</style>
